/**
 * The descent veil: one black layer over everything, driven by one opacity ramp.
 *
 * Generating and meshing a floor is a synchronous 100–400 ms freeze. As a hitch
 * it reads as a bug; behind a blackout it reads as noclipping out of the world,
 * which is what it is. `game.ts` owns the sequence — this only knows how to be
 * opaque.
 *
 * Opacity is the only property animated, deliberately: opacity transitions run
 * off the main thread, so the veil keeps closing smoothly through the very
 * frames where the generator has the thread locked. Anything that animated a
 * colour or a filter here would stall exactly when it is needed most.
 */

import { clamp01 } from '../core/math';
import { el } from './dom';

/**
 * The veil's control surface. `ui/ui.ts` exposes it alongside `UISystem` rather
 * than in `core/systems.ts`, which the level-flow code does not own; a widened
 * return type is still assignable to `MakeUISystem`.
 */
export interface Veil {
  /**
   * Ramp the veil toward `alpha` — 0 clear, 1 fully opaque — over `seconds`.
   * Zero seconds snaps, which is what cancelling a transition wants.
   */
  veil(alpha: number, seconds: number): void;
}

export class Fade implements Veil {
  readonly el = el('div', 'ui-veil');

  /**
   * When the current ramp finishes leaving the screen clear. `Infinity` while
   * the veil is on its way to opaque or sitting there, and a wall-clock stamp
   * once it has been told to open.
   */
  private clearAt = 0;

  /**
   * True whenever the veil is covering anything at all — through the whole
   * opening ramp, not just up to the moment it starts. The UI's own keyboard
   * shortcuts (the inventory, the journal, the quick slots) are window listeners
   * and so miss every gate `game.ts` puts on the simulation; this is what they
   * check instead. Flipping it when the *opening* began let Tab put the
   * inventory on screen at opacity 1.0, a full second before the new floor was
   * visible behind it.
   *
   * Derived from the clock rather than cleared by a timer on purpose: there is
   * no callback to lose, so a fade interrupted by a tab-out or a cancel cannot
   * strand the UI in a state where nothing opens.
   */
  get closed(): boolean {
    return performance.now() < this.clearAt;
  }

  /** Clears `.closed` when the opening ramp finishes. See `syncClosed`. */
  private clearTimer = 0;

  constructor() {
    // Purely presentational, and it covers content that is still in the tree.
    this.el.setAttribute('aria-hidden', 'true');
  }

  /**
   * Mirror `closed` onto a class, for the things CSS has to be able to see.
   *
   * `closed` is derived from the clock on purpose — there is no callback to
   * lose — but a stylesheet cannot read a getter, and the on-screen touch
   * controls have to go away while the screen is black or they float over the
   * descent with nothing behind them. So the class is a mirror, not the truth:
   * anything that can be wrong about it is wrong about a fade's *appearance*
   * for one timer, and nothing that gates behaviour reads it.
   */
  private syncClosed(): void {
    clearTimeout(this.clearTimer);
    this.clearTimer = 0;
    const until = this.clearAt;
    this.el.classList.toggle('closed', performance.now() < until);
    if (until === Infinity || until <= performance.now()) return;
    this.clearTimer = setTimeout(
      () => {
        this.clearTimer = 0;
        // Re-checked rather than assumed: another `veil` may have overtaken it.
        this.el.classList.toggle('closed', this.closed);
      },
      Math.max(0, until - performance.now())
    ) as unknown as number;
  }

  veil(alpha: number, seconds: number): void {
    const a = clamp01(alpha);
    // Zero seconds snaps, so a cancel gives the screen — and the shortcuts —
    // back on the same frame rather than after a ramp that is not running.
    this.clearAt = a > 0 ? Infinity : performance.now() + Math.max(0, seconds) * 1000;
    this.syncClosed();
    // Closing eases in, so the floor holds a moment and then goes; opening eases
    // out, so the new one arrives rather than pops. Symmetrical easing on a
    // full-screen black makes both halves feel like a loading screen.
    this.el.style.transitionTimingFunction = a > 0 ? 'ease-in' : 'ease-out';
    // Written unconditionally: the same alpha with a different ramp is a
    // meaningful call (a cancel snapping a fade that is already on its way out).
    this.el.style.transitionDuration = `${Math.max(0, seconds)}s`;
    this.el.style.opacity = String(a);
  }
}
